import {
  FETCH_START,
  FETCH_SUCCESS,
  FETCH_ERROR,
  SHOW_MESSAGE,
  GET_VIDEO_CONTENT,
  GET_VIDEO_CONTENT_ITEM,
  SET_VIDEO_PAGE,
  SET_VIDEO_SEARCH,
  SET_VIDEO_SORT,
  SET_VIDEO_PUBLISHED_FILTER,
} from '../../shared/constants/ActionTypes';
import jwtAxios from '@crema/services/auth/jwt-auth/jwt-api';
import {
  normalizeVideoRow,
  parseVideoListPayload,
} from 'pages/admin/Videos/videoMappers';

/** Let the client set multipart boundary (do not force application/json). */
export const multipartFormConfig = {
  transformRequest: [
    (data, headers) => {
      if (data instanceof FormData) {
        delete headers['Content-Type'];
      }
      return data;
    },
  ],
};

const MAX_PAGE_SIZE = 100;

const isApiSuccess = (res) => {
  if (res.status === 200 || res.status === 201) {
    return true;
  }
  const d = res.data;
  if (d == null || typeof d !== 'object') {
    return false;
  }
  if ('status' in d) {
    return d.status === true;
  }
  if ('succeeded' in d) {
    return d.succeeded === true;
  }
  return false;
};

/** ASP.NET ProblemDetails / validation error body → user-facing string. */
export function getApiErrorMessageFromData(data) {
  if (data == null) return null;
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (typeof data !== 'object') {
    return null;
  }

  if (data.errors && typeof data.errors === 'object') {
    const messages = [];
    Object.keys(data.errors).forEach((key) => {
      const value = data.errors[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item != null && String(item).trim()) {
            messages.push(String(item).trim());
          }
        });
      } else if (value != null && String(value).trim()) {
        messages.push(String(value).trim());
      }
    });
    if (messages.length) {
      return messages.join(' ');
    }
  }

  return (
    data.message ??
    data.Message ??
    data.error ??
    data.Error ??
    data.msg ??
    data.title ??
    data.Title ??
    null
  );
}

const getErrorPayload = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status;

  const fromBody = getApiErrorMessageFromData(data);
  if (fromBody) {
    return String(fromBody);
  }

  const base = error?.message ?? 'Something went wrong, Please try again!';
  if (status === 405) {
    return `${base} (HTTP 405 — check API method and path)`;
  }
  return base;
};

export const getVideoApiErrorMessage = getErrorPayload;

const unwrapVideo = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  return data.data ?? data.result ?? data.video ?? data;
};

export const setVideoPage = (page) => ({
  type: SET_VIDEO_PAGE,
  payload: page,
});

export const setVideoSearch = (search) => ({
  type: SET_VIDEO_SEARCH,
  payload: search,
});

export const setVideoSort = (sortBy) => ({
  type: SET_VIDEO_SORT,
  payload: sortBy,
});

/** @param {'all'|'published'|'unpublished'} filter */
export const setVideoPublishedFilter = (filter) => ({
  type: SET_VIDEO_PUBLISHED_FILTER,
  payload: filter,
});

/**
 * GET /videos — list with pagination, keyword, optional isPublished filter.
 */
export const getVideos = (limit = 10) => {
  return (dispatch, getState) => {
    const {page, search, publishedFilter} = getState().video;
    const pageSize = Math.min(Math.max(1, limit), MAX_PAGE_SIZE);
    const params = {
      pageNo: page,
      pageSize,
      keyword: search || '',
    };
    if (publishedFilter === 'published') {
      params.isPublished = true;
    } else if (publishedFilter === 'unpublished') {
      params.isPublished = false;
    }

    dispatch({type: FETCH_START});
    return jwtAxios
      .get('/videos', {params})
      .then((res) => {
        if (!isApiSuccess(res)) {
          dispatch({
            type: FETCH_ERROR,
            payload:
              res.data?.message ||
              res.data?.error ||
              'Something went wrong, Please try again!',
          });
          return;
        }
        dispatch({type: FETCH_SUCCESS});
        const {list, total} = parseVideoListPayload(res.data);
        dispatch({
          type: GET_VIDEO_CONTENT,
          payload: {
            videos: list.map(normalizeVideoRow),
            total,
          },
        });
      })
      .catch((error) => {
        dispatch({type: FETCH_ERROR, payload: getErrorPayload(error)});
      });
  };
};

/**
 * GET /video/{videoId} — single video (includes unpublished).
 */
export const getVideoById = (videoId) => {
  return (dispatch) => {
    dispatch({type: FETCH_START});
    return jwtAxios
      .get(`/video/${encodeURIComponent(videoId)}`)
      .then((res) => {
        if (!isApiSuccess(res)) {
          dispatch({
            type: FETCH_ERROR,
            payload:
              res.data?.message ||
              res.data?.error ||
              'Something went wrong, Please try again!',
          });
          return null;
        }
        dispatch({type: FETCH_SUCCESS});
        const video = normalizeVideoRow(unwrapVideo(res.data));
        dispatch({
          type: GET_VIDEO_CONTENT_ITEM,
          payload: video,
        });
        return video;
      })
      .catch((error) => {
        dispatch({type: FETCH_ERROR, payload: getErrorPayload(error)});
        return null;
      });
  };
};

/** POST /video — multipart create with video + optional thumbnail. */
export const addVideo = (formData, onDone) => {
  return (dispatch) => {
    dispatch({type: FETCH_START, payload: 'Uploading video…'});
    return jwtAxios
      .post('/video', formData, multipartFormConfig)
      .then((res) => {
        if (isApiSuccess(res)) {
          const msg = res.data?.message;
          return dispatch(getVideos()).then(() => {
            if (msg) {
              dispatch({type: SHOW_MESSAGE, payload: msg});
            }
            onDone?.();
            return res;
          });
        }
        const errMsg =
          getApiErrorMessageFromData(res.data) ||
          'Something went wrong, Please try again!';
        dispatch({type: FETCH_SUCCESS});
        return Promise.reject(Object.assign(new Error(errMsg), {response: res}));
      })
      .catch((error) => {
        dispatch({type: FETCH_SUCCESS});
        return Promise.reject(error);
      });
  };
};

/** PUT /video/{videoId} — update metadata, files, categories, publish/featured flags. */
export const updateVideo = (videoId, formData, onDone) => {
  return (dispatch) => {
    dispatch({type: FETCH_START, payload: 'Saving video…'});
    jwtAxios
      .put(`/video/${encodeURIComponent(videoId)}`, formData, multipartFormConfig)
      .then((res) => {
        if (isApiSuccess(res)) {
          const msg = res.data?.message;
          return dispatch(getVideos()).then(() => {
            if (msg) {
              dispatch({type: SHOW_MESSAGE, payload: msg});
            }
            onDone?.();
          });
        }
        dispatch({
          type: FETCH_ERROR,
          payload:
            res.data?.message ||
            res.data?.error ||
            'Something went wrong, Please try again!',
        });
      })
      .catch((error) => {
        dispatch({type: FETCH_ERROR, payload: getErrorPayload(error)});
      });
  };
};

/** DELETE /video/{videoId} — soft-delete and remove storage files. */
export const deleteVideo = (videoId) => {
  return (dispatch) => {
    dispatch({type: FETCH_START});
    jwtAxios
      .delete(`/video/${encodeURIComponent(videoId)}`)
      .then((res) => {
        if (isApiSuccess(res)) {
          dispatch({type: FETCH_SUCCESS});
          const msg = res.data?.message;
          if (msg) {
            dispatch({type: SHOW_MESSAGE, payload: msg});
          }
          dispatch(getVideos());
        } else {
          dispatch({
            type: FETCH_ERROR,
            payload:
              res.data?.message ||
              res.data?.error ||
              'Something went wrong, Please try again!',
          });
        }
      })
      .catch((error) => {
        dispatch({type: FETCH_ERROR, payload: getErrorPayload(error)});
      });
  };
};
